import { navigate } from 'gatsby'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import axios from '../../axios'
import { ENDPOINTS } from '../../constants'
import { generateEditPageLink } from '../../utils/generateEditPageLink'
import {
  CREATE_FAILURE_TOAST,
  CREATE_SUCCESS_MESSAGE,
  EMPTY_FOLDER_MESSAGE,
} from './constants'
import { getApiErrorMessage } from './getApiErrorMessage'
import {
  CreateEditPageFormState,
  CreateEditPageFromFolderPayload,
  CreateEditPageFromFolderResponseBody,
} from './types'
import { getInitialTitleError, validateEditPageTitle } from './validateTitle'

interface UseCreateEditPageFormParams {
  folderId: number | string
  initialTitle: string
  itemCount: number
  open: boolean
  onClose: () => void
  onCreated?: (editPageId: number) => void
}

interface UseCreateEditPageFormResult {
  title: string
  description: string
  titleError: string
  formError: string
  isSubmitting: boolean
  hasTitleError: boolean
  canSubmit: boolean
  handleClose: () => void
  handleTitleChange: (value: string) => void
  handleTitleBlur: () => void
  handleDescriptionChange: (value: string) => void
  handleSubmit: () => Promise<void>
}

const createInitialState = (initialTitle: string): CreateEditPageFormState => ({
  title: initialTitle,
  description: '',
  titleError: getInitialTitleError(initialTitle),
  formError: '',
  isSubmitting: false,
  titleTouched: false,
})

export const useCreateEditPageForm = ({
  folderId,
  initialTitle,
  itemCount,
  open,
  onClose,
  onCreated,
}: UseCreateEditPageFormParams): UseCreateEditPageFormResult => {
  const [state, setState] = useState<CreateEditPageFormState>(() =>
    createInitialState(initialTitle)
  )

  useEffect(() => {
    if (!open) return
    setState(createInitialState(initialTitle || ''))
  }, [open, initialTitle])

  const handleClose = useCallback(() => {
    if (state.isSubmitting) return
    onClose()
  }, [onClose, state.isSubmitting])

  const handleTitleChange = useCallback((value: string) => {
    setState(prev => {
      const shouldValidate = prev.titleTouched || value.length > 0
      const error = shouldValidate ? validateEditPageTitle(value) : null
      return {
        ...prev,
        title: value,
        formError: '',
        titleError: shouldValidate ? error || '' : prev.titleError,
      }
    })
  }, [])

  const handleTitleBlur = useCallback(() => {
    setState(prev => {
      const error = validateEditPageTitle(prev.title)
      return {
        ...prev,
        titleTouched: true,
        titleError: error || '',
      }
    })
  }, [])

  const handleDescriptionChange = useCallback((value: string) => {
    setState(prev => ({ ...prev, description: value }))
  }, [])

  const handleSubmit = useCallback(async () => {
    const trimmedTitle = state.title.trim()
    const titleError = validateEditPageTitle(trimmedTitle)

    setState(prev => ({
      ...prev,
      title: trimmedTitle,
      titleTouched: true,
      titleError: titleError || '',
    }))

    if (titleError) return

    if (!itemCount) {
      setState(prev => ({ ...prev, formError: EMPTY_FOLDER_MESSAGE }))
      return
    }

    const payload: CreateEditPageFromFolderPayload = {
      folderId: Number(folderId),
      title: trimmedTitle,
      description: state.description.trim(),
    }

    try {
      setState(prev => ({ ...prev, formError: '', isSubmitting: true }))

      const response = await axios.post<CreateEditPageFromFolderResponseBody>(
        ENDPOINTS.createEditPageFromFolder,
        payload
      )

      const editPageId = response?.data?.data?.editPageId
      if (typeof editPageId !== 'number') {
        throw new Error(CREATE_FAILURE_TOAST)
      }

      toast.success(CREATE_SUCCESS_MESSAGE)
      onCreated?.(editPageId)
      onClose()
      navigate(generateEditPageLink(trimmedTitle, editPageId))
    } catch (error) {
      setState(prev => ({
        ...prev,
        formError: getApiErrorMessage(error),
      }))
      toast.error(CREATE_FAILURE_TOAST)
    } finally {
      setState(prev => ({ ...prev, isSubmitting: false }))
    }
  }, [folderId, itemCount, onClose, onCreated, state.description, state.title])

  const hasTitleError = Boolean(state.titleError)

  return {
    title: state.title,
    description: state.description,
    titleError: state.titleError,
    formError: state.formError,
    isSubmitting: state.isSubmitting,
    hasTitleError,
    canSubmit: !state.isSubmitting && itemCount > 0 && !hasTitleError,
    handleClose,
    handleTitleChange,
    handleTitleBlur,
    handleDescriptionChange,
    handleSubmit,
  }
}
